/**
 * A family's money ANSWERS as staff read them (#2719), and as the family reads
 * them back on the application's review step (#2948).
 *
 * A Canadian family answers its money questions in Canadian dollars ("Monthly
 * income (CAD)", "CA$ per month"), a US family in US dollars. The staff page
 * shortens those labels and drops the unit, so the answer itself carries it:
 * "$1,750" for a US family, "CA$1,750" for a Canadian one. This mirrors
 * `answerCurrency` in backend/src/services/committeeOnePager/content.js
 * (#2523), so the staff page and the committee one-pager read alike.
 *
 * The FAMILY's answers only. A grant amount TRF pays goes through
 * `formatMoney` in `lib/money.ts`, in the award's own currency.
 */

/** The answers a family typed as dollars, by `<section>.<field>` path. */
export const MONEY_ANSWER_PATHS: ReadonlySet<string> = new Set([
  'finances.monthly_income_usd',
  'finances.expenses.rent',
  'finances.expenses.utilities',
  'finances.expenses.transportation',
  'finances.expenses.food',
  'finances.expenses.childcare',
  'finances.expenses.medical',
  'finances.expenses.other',
  'grant_request.amount_requested_usd',
  'grant_request.expense_amount_per_bill',
]);

/**
 * "CA$" for a Canadian family, "$" otherwise, including an unknown country,
 * as the committee one-pager’s `answerCurrency` does.
 */
export function answerCurrencySymbol(country: unknown): string | null {
  if (typeof country !== 'string' || country.trim() === '') return '$'; // as the one-pager’s answerCurrency does
  return country.trim().toUpperCase() === 'CA' ? 'CA$' : '$';
}

/**
 * "$1,750" · "CA$1,750" · "$350.50" (cents kept only where the answer,
 * rounded to the cent, has them: 350.004 prints "$350"). The same rule as the
 * backend's `amountShowsCents` (backend/src/lib/moneyCents.js), which the
 * one-pager and the grant note use, so one answer reads one way everywhere.
 * A value that is not a finite number returns null.
 *
 * `locale` groups the digits (#2948): staff read en-US; the family review
 * passes the reader's language, so "4500" never reads as "4,500" to a reader
 * for whom the comma is the decimal mark.
 */
export function formatMoneyAnswer(value: unknown, symbol: string, locale = 'en-US'): string | null {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const fraction = Math.round(n * 100) % 100 !== 0 ? 2 : 0;
  return `${symbol}${n.toLocaleString(locale, { minimumFractionDigits: fraction, maximumFractionDigits: fraction })}`;
}
